// =============================================================================
// BrightPath - Health Log Types React Query Hooks
// Package: BP-03-04-P1 (Health Frontend - Foundation)
// =============================================================================

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { healthApi } from '@/features/health/api/healthApi';
import type { HealthLogType, HealthLogTypeSlug } from '@/features/health/types/healthTypes';

// =============================================================================
// Query Keys
// =============================================================================

export const healthLogTypeKeys = {
  all: ['healthLogTypes'] as const,
  list: () => [...healthLogTypeKeys.all, 'list'] as const,
  detail: (idOrSlug: string) => [...healthLogTypeKeys.all, 'detail', idOrSlug] as const,
};

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetch all available health log types
 */
export function useHealthLogTypes(
  options?: Omit<UseQueryOptions<HealthLogType[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: healthLogTypeKeys.list(),
    queryFn: () => healthApi.getHealthLogTypes(),
    staleTime: 1000 * 60 * 60, // 1 hour (rarely changes)
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    ...options,
  });
}

/**
 * Fetch a single health log type by ID or slug
 */
export function useHealthLogType(
  idOrSlug: string | undefined,
  options?: Omit<UseQueryOptions<HealthLogType, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: healthLogTypeKeys.detail(idOrSlug!),
    queryFn: () => healthApi.getHealthLogType(idOrSlug!),
    enabled: !!idOrSlug,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
    ...options,
  });
}

// =============================================================================
// Selectors / Utilities
// =============================================================================

/**
 * Get a health log type by slug from the cached list
 */
export function useHealthLogTypeBySlug(slug: HealthLogTypeSlug) {
  const { data: types } = useHealthLogTypes();
  return types?.find((t) => t.slug === slug);
}

/**
 * Get health log type color by slug
 */
export function useHealthLogTypeColor(slug: HealthLogTypeSlug): string {
  const type = useHealthLogTypeBySlug(slug);
  return type?.color ?? '#8B8178'; // fallback to warm-gray
}

/**
 * Get health log type icon by slug
 */
export function useHealthLogTypeIcon(slug: HealthLogTypeSlug): string {
  const type = useHealthLogTypeBySlug(slug);
  return type?.icon ?? 'FileText'; // fallback icon
}

// =============================================================================
// Static Fallback Data (for SSR/initial render)
// =============================================================================

export const HEALTH_LOG_TYPE_DEFAULTS: Record<
  HealthLogTypeSlug,
  { name: string; icon: string; color: string }
> = {
  seizure: { name: 'Seizure', icon: 'Zap', color: '#E57373' },
  medication: { name: 'Medication', icon: 'Pill', color: '#C4785A' },
  symptom: { name: 'Symptom', icon: 'Thermometer', color: '#FFB74D' },
  sleep: { name: 'Sleep', icon: 'Moon', color: '#6B8DD6' },
  feeding: { name: 'Feeding', icon: 'UtensilsCrossed', color: '#81C784' },
  therapy: { name: 'Therapy', icon: 'Activity', color: '#7A9E7E' },
  vital: { name: 'Vital Signs', icon: 'Heart', color: '#F06292' },
  mood: { name: 'Mood', icon: 'Smile', color: '#9575CD' },
};

/**
 * Get static type info (doesn't require API call)
 */
export function getStaticLogTypeInfo(slug: HealthLogTypeSlug) {
  return HEALTH_LOG_TYPE_DEFAULTS[slug];
}

// =============================================================================
// Type Groupings
// =============================================================================

/**
 * Get health log types grouped by category
 */
export function useHealthLogTypesGrouped() {
  const { data: types, isLoading } = useHealthLogTypes();
  
  if (!types) {
    return { grouped: null, isLoading };
  }
  
  const grouped = {
    medical: types.filter((t) =>
      ['seizure', 'medication', 'vital', 'symptom'].includes(t.slug)
    ),
    daily: types.filter((t) =>
      ['sleep', 'feeding', 'mood'].includes(t.slug)
    ),
    therapy: types.filter((t) => t.slug === 'therapy'),
  };
  
  return { grouped, isLoading };
}

// =============================================================================
// Fields Schema Helpers
// =============================================================================

/**
 * Get the fields schema for a specific log type
 */
export function useLogTypeFields(slug: HealthLogTypeSlug) {
  const { data: types, isLoading } = useHealthLogTypes();
  const type = types?.find((t) => t.slug === slug);
  
  return {
    fields: type?.fieldsSchema ?? [],
    isLoading,
  };
}

/**
 * Check if a log type supports severity
 */
export function logTypeHasSeverity(slug: HealthLogTypeSlug): boolean {
  const typesWithSeverity: HealthLogTypeSlug[] = [
    'seizure',
    'symptom',
    'mood',
  ];
  return typesWithSeverity.includes(slug);
}

/**
 * Check if a log type supports duration
 */
export function logTypeHasDuration(slug: HealthLogTypeSlug): boolean {
  const typesWithDuration: HealthLogTypeSlug[] = [
    'seizure',
    'sleep',
    'feeding',
    'therapy',
  ];
  return typesWithDuration.includes(slug);
}

// =============================================================================
// Display Helpers
// =============================================================================

/**
 * Get formatted display name for a log type
 */
export function getLogTypeDisplayName(
  type: HealthLogType | HealthLogTypeSlug
): string {
  if (typeof type === 'string') {
    return HEALTH_LOG_TYPE_DEFAULTS[type]?.name ?? type;
  }
  return type.name;
}

/**
 * Get icon component name for a log type
 */
export function getLogTypeIconName(
  type: HealthLogType | HealthLogTypeSlug
): string {
  if (typeof type === 'string') {
    return HEALTH_LOG_TYPE_DEFAULTS[type]?.icon ?? 'FileText';
  }
  return type.icon;
}

/**
 * Get color for a log type
 */
export function getLogTypeColor(
  type: HealthLogType | HealthLogTypeSlug
): string {
  if (typeof type === 'string') {
    return HEALTH_LOG_TYPE_DEFAULTS[type]?.color ?? '#8B8178';
  }
  return type.color;
}

// =============================================================================
// Severity Helpers
// =============================================================================

export type SeverityLevel = 'low' | 'medium' | 'high' | 'critical';

export interface SeverityInfo {
  level: SeverityLevel;
  label: string;
  color: string;
  bgColor: string;
}

/**
 * Get severity info for a given severity value (1-10)
 */
export function getSeverityInfo(severity: number): SeverityInfo {
  if (severity <= 3) {
    return {
      level: 'low',
      label: 'Low',
      color: '#4CAF9E',
      bgColor: 'rgba(76, 175, 158, 0.15)',
    };
  }
  if (severity <= 5) {
    return {
      level: 'medium',
      label: 'Moderate',
      color: '#FFB74D',
      bgColor: 'rgba(255, 183, 77, 0.15)',
    };
  }
  if (severity <= 7) {
    return {
      level: 'high',
      label: 'High',
      color: '#FF8A65',
      bgColor: 'rgba(255, 138, 101, 0.15)',
    };
  }
  return {
    level: 'critical',
    label: 'Critical',
    color: '#E57373',
    bgColor: 'rgba(229, 115, 115, 0.15)',
  };
}

// =============================================================================
// Export all hooks for barrel export
// =============================================================================

export {
  useHealthLogTypes as default,
};

